import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Card, Input, StatusPill, useToast } from '../../design-system';
import '../styles/ClientAccess.css';

// Admin-only card on the employee profile: which clients this employee is
// BLOCKED from. Blocklist semantics — a ticked client is HIDDEN from the
// employee everywhere (lists, search, reports); unticked stays visible. The
// management API is Admin-gated, so for a manager/associate viewer the GET
// returns 403 and this card renders nothing (it simply doesn't appear).
export default function ClientAccessManager({ userId }) {
  const toast = useToast();
  const [allowed, setAllowed] = useState(true); // false once the API says 403
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [blocked, setBlocked] = useState(() => new Set());
  const [initial, setInitial] = useState('[]'); // snapshot to detect changes
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [accessRes, clientsRes] = await Promise.all([
          apiClient.get(`/api/client-access/${userId}`),
          apiClient.get('/api/clients/options'),
        ]);
        if (cancelled) return;
        const ids = (accessRes.data.restrictedClientIds || []).map(String);
        setBlocked(new Set(ids));
        setInitial(JSON.stringify([...ids].sort()));
        setClients(clientsRes.data || []);
        setAllowed(true);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 403) setAllowed(false);
        else toast.danger('Failed to load client access');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const dirty = useMemo(() => JSON.stringify([...blocked].sort()) !== initial, [blocked, initial]);

  const toggle = (id) =>
    setBlocked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) => (c.displayName || '').toLowerCase().includes(q) || (c.clientCode || '').toLowerCase().includes(q)
    );
  }, [clients, search]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put(`/api/client-access/${userId}`, { restrictedClientIds: [...blocked] });
      const ids = (res.data.restrictedClientIds || []).map(String);
      setBlocked(new Set(ids));
      setInitial(JSON.stringify([...ids].sort()));
      toast.success(ids.length ? `Blocked ${ids.length} client${ids.length > 1 ? 's' : ''}` : 'Access restrictions cleared');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) return null; // non-admins never see this card

  return (
    <Card className="profile-section">
      <Card.Header
        title="Client Access"
        subtitle="Tick a client to hide it from this employee. Unticked clients stay visible. Applies everywhere — lists, search, reports."
        action={
          <Button variant="primary" size="sm" onClick={save} loading={saving} disabled={!dirty}>
            Save
          </Button>
        }
      />
      <div className="client-access-toolbar">
        <Input placeholder="Search clients…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <StatusPill variant={blocked.size ? 'warning' : 'neutral'} label={`${blocked.size} blocked`} />
      </div>
      {loading ? (
        <p className="client-access-hint">Loading…</p>
      ) : clients.length === 0 ? (
        <p className="client-access-hint">No clients yet.</p>
      ) : (
        <div className="client-access-list">
          {filtered.map((c) => {
            const id = String(c._id);
            const isBlocked = blocked.has(id);
            return (
              <label key={id} className={`client-access-row${isBlocked ? ' blocked' : ''}`}>
                <input type="checkbox" checked={isBlocked} onChange={() => toggle(id)} />
                <span className="client-access-name">{c.displayName}</span>
                {c.clientCode && <span className="client-access-code">{c.clientCode}</span>}
                {isBlocked && <StatusPill variant="danger" label="Blocked" />}
              </label>
            );
          })}
          {filtered.length === 0 && <p className="client-access-hint">No clients match “{search}”.</p>}
        </div>
      )}
    </Card>
  );
}
